import {useEffect, useState} from "react";
import {connect} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css} from "@panda/css";
import lscache from "lscache";
import {bindActionCreators, Dispatch} from "redux";

import {WideContent} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Tabs} from "@pg-design/tabs-module";
import {Text} from "@pg-design/text-module";
import {createFormActions, createFormActionTypes, FormActions} from "@pg-mono/form";

import {scrollToTop} from "../../app/components/ScrollToTop";
import {PROPERTY_CONFIGURATOR_LOCATION} from "../../app/constants/storage_keys";
import {IRPStore} from "../../app/rp_reducer";
import {googlePlacesDivId} from "../../search/utils/search_constants";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {GTMEvent} from "../analytics/events";
import {BudgetTab} from "../components/BudgetTab";
import {LocationTab} from "../components/LocationTab";
import {RoomsTab} from "../components/RoomsTab";
import {usePropertyConfiguratorFiltersAnalytics} from "../hooks/use_property_configurator_filters_analytics";
import {usePropertyConfiguratorStepAnalytics} from "../hooks/use_property_configurator_step_analytics";
import {propertyConfiguratorSearchFormFields} from "../reducers/property_configurator_reducer";
import {PropertyConfiguratorSearchFormValuesType} from "../types/PropertyConfiguratorSearchFormValuesType";
import {PropertyConfiguratorStep} from "../types/PropertyConfiguratorStep";
import {getPropertyConfiguratorUrl} from "../utils/get_url_with_params";
import {parseQueryParams} from "../utils/parse_query_params";
import {getPropertyConfiguratorStorageData, setPropertyConfiguratorStorageData} from "../utils/property_configurator_storage";
import {ResultsList} from "./ResultsList";

const configSteps = [
    {
        title: "Lokalizacja",
        value: PropertyConfiguratorStep.LOCATION
    },
    {
        title: "Pomieszczenia i piętra",
        value: PropertyConfiguratorStep.ROOMS
    },
    {
        title: "Budżet",
        value: PropertyConfiguratorStep.BUDGET
    }
];

export interface IStateProps {
    formValues: PropertyConfiguratorSearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, unknown>>;
}

interface IProps extends IStateProps, IActionsProps {}

export const PropertyConfiguratorC = (props: IProps) => {
    const history = useHistory();
    const location = useLocation();
    const {trackAftermarket, trackLoan, trackLocalisation, trackPrice} = usePropertyConfiguratorFiltersAnalytics(GTMEvent.FLAT_CONFIG_MODAL);

    const [currentStep, setCurrentStep] = useState<null | PropertyConfiguratorStep>(null);

    const handleSaveValuesToLS = (values: PropertyConfiguratorSearchFormValuesType) => {
        history.push(getPropertyConfiguratorUrl(values));
        setPropertyConfiguratorStorageData({
            ...values,
            search: {
                ...values.search,
                label: ""
            }
        });
    };

    useEffect(() => {
        scrollToTop();
    }, [currentStep]);

    const trackStepChange = usePropertyConfiguratorStepAnalytics();

    useEffect(() => {
        const parsedQueryValues = parseQueryParams(location.search);
        const lsValues = getPropertyConfiguratorStorageData();
        const values = location.search ? parsedQueryValues : lsValues;
        const propertyConfiguratorLocation = lscache.get(PROPERTY_CONFIGURATOR_LOCATION) || {};

        if (Object.keys(values).length) {
            setTimeout(() => {
                props.formActions.update({...values, ...(propertyConfiguratorLocation?.regions?.length && {search: propertyConfiguratorLocation})});
                if (propertyConfiguratorLocation?.regions?.length) {
                    lscache.remove(PROPERTY_CONFIGURATOR_LOCATION);
                }
                setCurrentStep(PropertyConfiguratorStep.RESULTS);
            }, 1);
        } else {
            setCurrentStep(PropertyConfiguratorStep.LOCATION);
            trackStepChange(PropertyConfiguratorStep.LOCATION);
        }
    }, []);

    useEffect(() => {
        if (currentStep === PropertyConfiguratorStep.RESULTS) {
            handleSaveValuesToLS(props.formValues);
        }
    }, [currentStep]);

    const onSetCurrentStep = (step: PropertyConfiguratorStep, prev = false) => {
        if (currentStep === PropertyConfiguratorStep.LOCATION) {
            trackLocalisation();
        }
        if (currentStep === PropertyConfiguratorStep.BUDGET) {
            trackAftermarket();
            trackLoan();
            trackPrice();
        }
        if (!prev) {
            setTimeout(() => trackStepChange(step), 1);
        }
        setCurrentStep(step);
    };

    const stepRenderer = () => {
        switch (currentStep) {
            case PropertyConfiguratorStep.LOCATION:
                return <LocationTab handleTabChange={onSetCurrentStep} />;
            case PropertyConfiguratorStep.ROOMS:
                return <RoomsTab handleTabChange={onSetCurrentStep} values={props.formValues} onChange={props.formActions.update} />;
            case PropertyConfiguratorStep.BUDGET:
                return <BudgetTab handleTabChange={onSetCurrentStep} values={props.formValues} onChange={props.formActions.update} />;
        }
    };

    if (!currentStep) {
        return (
            <WideContent className={noStep}>
                <Loader padding="xxl" size="lg" />
            </WideContent>
        );
    }

    return (
        <>
            <div id={googlePlacesDivId} />
            {currentStep === PropertyConfiguratorStep.RESULTS ? (
                <ResultsList handleStoreValues={handleSaveValuesToLS} />
            ) : (
                <WideContent className={contentStyle}>
                    <Text variant="headline_3" mb={4}>
                        Konfigurator mieszkania
                    </Text>
                    <Tabs activeColor="gray" fullWidth theme="gray" tabs={configSteps} value={currentStep} variant="contained" onChange={setCurrentStep} />
                    <div className={whiteWrapper}>{stepRenderer()}</div>
                </WideContent>
            )}
        </>
    );
};

export const PropertyConfigurator = connect(mapStateToProps, mapActionsToProps)(PropertyConfiguratorC);

const search_actions = "property_configurator/SEARCH_FORM";
export const searchFormTypes = createFormActionTypes(search_actions);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const searchFormActions = createFormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, any>>(searchFormTypes);

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const whiteWrapper = css({
    backgroundColor: "var(--colors-white)",
    px: 2,
    py: 4,
    borderRadius: 4,
    mt: 3,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "normal",

    md: {
        px: 4
    }
});

const noStep = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100%"
});

const contentStyle = css({
    backgroundColor: "var(--colors-gray-100)",
    mb: 8,
    pt: 3,
    px: 0,

    md: {
        pt: 8
    }
});
import {useEffect, useState} from "react";
import {connect} from "react-redux";
import {useHistory, useLocation} from "react-router";
import {css} from "@panda/css";
import lscache from "lscache";
import {bindActionCreators, Dispatch} from "redux";

import {WideContent} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Tabs} from "@pg-design/tabs-module";
import {Text} from "@pg-design/text-module";
import {createFormActions, createFormActionTypes, FormActions} from "@pg-mono/form";

import {scrollToTop} from "../../app/components/ScrollToTop";
import {PROPERTY_CONFIGURATOR_LOCATION} from "../../app/constants/storage_keys";
import {IRPStore} from "../../app/rp_reducer";
import {googlePlacesDivId} from "../../search/utils/search_constants";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {GTMEvent} from "../analytics/events";
import {BudgetTab} from "../components/BudgetTab";
import {LocationTab} from "../components/LocationTab";
import {RoomsTab} from "../components/RoomsTab";
import {usePropertyConfiguratorFiltersAnalytics} from "../hooks/use_property_configurator_filters_analytics";
import {usePropertyConfiguratorStepAnalytics} from "../hooks/use_property_configurator_step_analytics";
import {propertyConfiguratorSearchFormFields} from "../reducers/property_configurator_reducer";
import {PropertyConfiguratorSearchFormValuesType} from "../types/PropertyConfiguratorSearchFormValuesType";
import {PropertyConfiguratorStep} from "../types/PropertyConfiguratorStep";
import {getPropertyConfiguratorUrl} from "../utils/get_url_with_params";
import {parseQueryParams} from "../utils/parse_query_params";
import {getPropertyConfiguratorStorageData, setPropertyConfiguratorStorageData} from "../utils/property_configurator_storage";
import {ResultsList} from "./ResultsList";

const configSteps = [
    {
        title: "Lokalizacja",
        value: PropertyConfiguratorStep.LOCATION
    },
    {
        title: "Pomieszczenia i piętra",
        value: PropertyConfiguratorStep.ROOMS
    },
    {
        title: "Budżet",
        value: PropertyConfiguratorStep.BUDGET
    }
];

export interface IStateProps {
    formValues: PropertyConfiguratorSearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}

interface IActionsProps {
    formActions: FormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, unknown>>;
}

interface IProps extends IStateProps, IActionsProps {}

export const PropertyConfiguratorC = (props: IProps) => {
    const history = useHistory();
    const location = useLocation();
    const {trackAftermarket, trackLoan, trackLocalisation, trackPrice} = usePropertyConfiguratorFiltersAnalytics(GTMEvent.FLAT_CONFIG_MODAL);

    const [currentStep, setCurrentStep] = useState<null | PropertyConfiguratorStep>(null);

    const handleSaveValuesToLS = (values: PropertyConfiguratorSearchFormValuesType) => {
        history.push(getPropertyConfiguratorUrl(values));
        setPropertyConfiguratorStorageData({
            ...values,
            search: {
                ...values.search,
                label: ""
            }
        });
    };

    useEffect(() => {
        scrollToTop();
    }, [currentStep]);

    const trackStepChange = usePropertyConfiguratorStepAnalytics();

    useEffect(() => {
        const parsedQueryValues = parseQueryParams(location.search);
        const lsValues = getPropertyConfiguratorStorageData();
        const values = location.search ? parsedQueryValues : lsValues;
        const propertyConfiguratorLocation = lscache.get(PROPERTY_CONFIGURATOR_LOCATION) || {};

        if (Object.keys(values).length) {
            setTimeout(() => {
                props.formActions.update({...values, ...(propertyConfiguratorLocation?.regions?.length && {search: propertyConfiguratorLocation})});
                if (propertyConfiguratorLocation?.regions?.length) {
                    lscache.remove(PROPERTY_CONFIGURATOR_LOCATION);
                }
                setCurrentStep(PropertyConfiguratorStep.RESULTS);
            }, 1);
        } else {
            setCurrentStep(PropertyConfiguratorStep.LOCATION);
            trackStepChange(PropertyConfiguratorStep.LOCATION);
        }
    }, []);

    useEffect(() => {
        if (currentStep === PropertyConfiguratorStep.RESULTS) {
            handleSaveValuesToLS(props.formValues);
        }
    }, [currentStep]);

    const onSetCurrentStep = (step: PropertyConfiguratorStep, prev = false) => {
        if (currentStep === PropertyConfiguratorStep.LOCATION) {
            trackLocalisation();
        }
        if (currentStep === PropertyConfiguratorStep.BUDGET) {
            trackAftermarket();
            trackLoan();
            trackPrice();
        }
        if (!prev) {
            setTimeout(() => trackStepChange(step), 1);
        }
        setCurrentStep(step);
    };

    const stepRenderer = () => {
        switch (currentStep) {
            case PropertyConfiguratorStep.LOCATION:
                return <LocationTab handleTabChange={onSetCurrentStep} />;
            case PropertyConfiguratorStep.ROOMS:
                return <RoomsTab handleTabChange={onSetCurrentStep} values={props.formValues} onChange={props.formActions.update} />;
            case PropertyConfiguratorStep.BUDGET:
                return <BudgetTab handleTabChange={onSetCurrentStep} values={props.formValues} onChange={props.formActions.update} />;
        }
    };

    if (!currentStep) {
        return (
            <WideContent className={noStep}>
                <Loader padding="xxl" size="lg" />
            </WideContent>
        );
    }

    return (
        <>
            <div id={googlePlacesDivId} />
            {currentStep === PropertyConfiguratorStep.RESULTS ? (
                <ResultsList handleStoreValues={handleSaveValuesToLS} />
            ) : (
                <WideContent className={contentStyle}>
                    <Text variant="headline_3" mb={4}>
                        Konfigurator mieszkania
                    </Text>
                    <Tabs activeColor="gray" fullWidth theme="gray" tabs={configSteps} value={currentStep} variant="contained" onChange={setCurrentStep} />
                    <div className={whiteWrapper}>{stepRenderer()}</div>
                </WideContent>
            )}
        </>
    );
};

export const PropertyConfigurator = connect(mapStateToProps, mapActionsToProps)(PropertyConfiguratorC);

const search_actions = "property_configurator/SEARCH_FORM";
export const searchFormTypes = createFormActionTypes(search_actions);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const searchFormActions = createFormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, any>>(searchFormTypes);

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const whiteWrapper = css({
    backgroundColor: "var(--colors-white)",
    px: 2,
    py: 4,
    borderRadius: 4,
    mt: 3,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "normal",

    md: {
        px: 4
    }
});

const noStep = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100%"
});

const contentStyle = css({
    backgroundColor: "var(--colors-gray-100)",
    mb: 8,
    pt: 3,
    px: 0,

    md: {
        pt: 8
    }
});
